import { authFetch, captureAuthSessionOwner, isAuthSessionOwnerCurrent } from "@/shared/auth/session";
import { currentAccountStorageScope } from "@/shared/storage/account-scope";
import { bufferedFraction, finiteDuration } from "./audio-media-utils.mjs";
import { dailyDefaultEditionItemBlob } from "./daily-default-edition-audio-contract.mjs";
import { buildStandardBriefPreviewUrl, standardBriefPreviewAudioKey, standardBriefPreviewSelection, validateStandardBriefPreviewManifest, type StandardBriefPreviewSelection } from "./standard-brief-preview-policy.mjs";
import COPY from "./standard-brief-preview-copy.json";
import type { AudioPlaybackState } from "./audio-playback-state";
export { standardBriefPreviewAudioKey } from "./standard-brief-preview-policy.mjs";

type Runtime = {
  beginRequest: () => { signal: AbortSignal; isCurrent: () => boolean; finish: () => void };
  createPlayer: () => HTMLAudioElement;
  clearPlayer: (player: HTMLAudioElement, objectUrl: string | null, preserve?: boolean) => void;
  attachEvents: (player: HTMLAudioElement, opts: { key: string; label: string; lang?: string }) => { armContent: (url: string) => void };
  replaceObjectUrl: (url: string) => void;
  setMediaSession: (label: string, detail: string, language: string) => void;
  setState: (state: AudioPlaybackState) => void;
  getState: () => AudioPlaybackState;
  getRate: () => number;
  clearEditionContext: () => void;
};
let runtime: Runtime | null = null;
export function installStandardBriefPreviewRuntime(value: Runtime) { runtime = value; }
export type StandardBriefPreviewOptions = StandardBriefPreviewSelection & { sectionTitle?: string; isSelectionCurrent?: () => boolean };

async function readManifest(response: Response): Promise<unknown> {
  const maximum = 18 * 1024 * 1024;
  if (!response.ok || Number(response.headers.get("content-length")) > maximum || !response.body) throw new Error("standard_brief_preview_unavailable");
  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let size = 0;
  try {
    while (true) {
      const part = await reader.read();
      if (part.done) break;
      size += part.value.byteLength;
      if (size > maximum) throw new Error("standard_brief_preview_too_large");
      chunks.push(part.value);
    }
  } finally { await reader.cancel().catch(() => undefined); }
  return JSON.parse(await new Blob(chunks).text()) as unknown;
}

/** Standard is the exact published first section. This cache-only GET cannot
 * generate speech, change lineup, save a recording, or complete a Brief. */
export async function playStandardBriefPreview(options: StandardBriefPreviewOptions): Promise<AudioPlaybackState> {
  if (!runtime) throw new Error("audio_player_unavailable");
  const playerRuntime = runtime;
  const owner = captureAuthSessionOwner();
  const storageScope = currentAccountStorageScope();
  const ownership = playerRuntime.beginRequest();
  playerRuntime.clearEditionContext();
  const player = playerRuntime.createPlayer();
  const key = standardBriefPreviewAudioKey(options);
  const copy = (COPY as Record<string, typeof COPY.en>)[options.languageCode] || COPY.en;
  const label = options.sectionTitle || copy["First section preview"];
  const controller = new AbortController();
  const abort = () => controller.abort();
  ownership.signal.addEventListener("abort", abort, { once: true });
  const timeout = window.setTimeout(abort, 20_000);
  const ownerCurrent = () => currentAccountStorageScope() === storageScope && (owner ? isAuthSessionOwnerCurrent(owner) : captureAuthSessionOwner() === null);
  const ownsPlayback = () => ownership.isCurrent() && ownerCurrent();
  const current = () => ownsPlayback() && (!options.isSelectionCurrent || options.isSelectionCurrent());
  let objectUrl: string | null = null;
  playerRuntime.setState({ phase: "loading", key, label });
  const discardStale = () => {
    const owns = ownsPlayback();
    playerRuntime.clearPlayer(player, objectUrl, !owns);
    if (owns) { ownership.finish(); playerRuntime.setState({ phase: "idle" }); }
    return playerRuntime.getState();
  };
  try {
    const selection = standardBriefPreviewSelection(options);
    if (!current()) return discardStale();
    const manifestPath = buildStandardBriefPreviewUrl(selection);
    const manifestUrl = new URL(manifestPath, window.location.origin).href;
    const response = await authFetch(manifestPath, { method: "GET", activitySource: "user_initiated", signal: controller.signal,
      cache: "no-store", redirect: "error", headers: { "Cache-Control": "no-cache" } });
    if (!current()) return discardStale();
    if (response.url !== manifestUrl) throw new Error("standard_brief_preview_untrusted_source");
    const edition = validateStandardBriefPreviewManifest(await readManifest(response), selection, response.headers.get("date"));
    if (!current()) return discardStale();
    const blob = dailyDefaultEditionItemBlob(edition.items[0].audioContent);
    const head = new Uint8Array(await blob.slice(0, 3).arrayBuffer());
    if (blob.size < 32 || !(head[0] === 0x49 && head[1] === 0x44 && head[2] === 0x33) && !(head[0] === 0xff && (head[1] & 0xe0) === 0xe0)) throw new Error("standard_brief_preview_invalid_audio");
    if (!current()) return discardStale();
    objectUrl = URL.createObjectURL(blob);
    playerRuntime.replaceObjectUrl(objectUrl);
    const events = playerRuntime.attachEvents(player, { key, label, lang: selection.languageCode });
    player.loop = false; player.src = objectUrl; events.armContent(objectUrl); player.load(); player.playbackRate = playerRuntime.getRate();
    await player.play();
    if (!current()) return discardStale();
    const detail = `${copy["First section preview"]} · ${edition.dateKey}`;
    playerRuntime.setMediaSession(label, detail, selection.languageCode);
    const next: AudioPlaybackState = { phase: "playing", key, label, detail, paused: false, buffering: false,
      positionMs: 0, durationMs: Math.round(finiteDuration(player) * 1000), buffered01: bufferedFraction(player),
      playbackRate: playerRuntime.getRate(), cache: "published-preview", canDownload: false };
    playerRuntime.setState(next); ownership.finish();
    return next;
  } catch (error) {
    if (!current()) return discardStale();
    playerRuntime.clearPlayer(player, objectUrl);
    ownership.finish();
    const unsupported = error instanceof Error && error.message === "standard_brief_preview_unsupported";
    const next: AudioPlaybackState = { phase: "error", key, label, code: "standard_brief_preview_unavailable", showSubscribeCta: false,
      detail: unsupported ? copy["A published recording isn’t available for your first selected section. You can read your Brief or choose a different lineup."]
        : copy["Your selected section’s recording isn’t available right now. You can try again or read your Brief."] };
    playerRuntime.setState(next);
    return next;
  } finally { window.clearTimeout(timeout); ownership.signal.removeEventListener("abort", abort); }
}
